import React, { useState, useEffect } from 'react';
import { userAPI, planAPI, entityAPI, smsAPI, pricingAPI } from '../../../services/api';
import { PlusIcon, PencilIcon, TrashIcon, KeyIcon, CheckCircleIcon, XCircleIcon, LockClosedIcon, DocumentTextIcon, BuildingOfficeIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import * as Icons from 'lucide-react';
import Pagination from '../../../components/common/Pagination'; // PROMPT-055
import { getApiBaseUrl } from '../../../config/apiBase';
import { formatIndianCurrency } from '../../../utils/numberFormat';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // PROMPT-055: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPlanMappingModal, setShowPlanMappingModal] = useState(false);
  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [apiKeyUser, setApiKeyUser] = useState(null);
  const [newAPIKey, setNewAPIKey] = useState('');
  const [copiedAPIKey, setCopiedAPIKey] = useState(false);
  const [apiKeyLength, setApiKeyLength] = useState(8);
  const [apiKeyLengthError, setApiKeyLengthError] = useState('');
  const [showEntityMappingModal, setShowEntityMappingModal] = useState(false);
  const [entityMappingUser, setEntityMappingUser] = useState(null);
  const [allEntities, setAllEntities] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [userEntities, setUserEntities] = useState([]);
  const [entitySearchTerm, setEntitySearchTerm] = useState('');
  // PROMPT-134: SMPP Configuration State
  const [showSMPPModal, setShowSMPPModal] = useState(false);
  const [smppUser, setSmppUser] = useState(null);
  const [smppConfig, setSmppConfig] = useState({
    system_id: '',
    password: '',
    bind_type: 'TRX',
    tps_limit: 10,
    allowed_ips: '',
    port: 2775,
    system_type: '',
    enforce_balance_check: false,
    status: 'disabled'
  });
  const [smppError, setSmppError] = useState('');
  const [showSMPPPassword, setShowSMPPPassword] = useState(false);
  // SMS Rate Modal State
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateUser, setRateUser] = useState(null);
  const [rateFormData, setRateFormData] = useState({ price_per_sms: '', currency: 'INR' });
  const [currentRate, setCurrentRate] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [mappingUser, setMappingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [plans, setPlans] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [showInactiveSubs, setShowInactiveSubs] = useState(false);
  const [mappingFormData, setMappingFormData] = useState({
    plan_id: '',
    auto_renew: false
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    full_name: '',
    mobile: '',
    company_name: '',
    tps_limit: 10,
    permission_ids: [],
    gui_allowed_ips: ''
  });
  const [permissions, setPermissions] = useState([]);
  const [showAdvancedRole, setShowAdvancedRole] = useState(false);


  useEffect(() => {
    fetchUsers();
    fetchPlans();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await smsAPI.getPermissionsGrouped();
      setPermissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
    }
  };

  // Fetch ALL users once (server returns paginated, so request large page_size)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll(1, 100);
      
      const usersData = response.data?.data || [];
      setUsers(usersData);
    } catch (error) {
      setError('Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await planAPI.getAll();
      // PROMPT-130: Handle response format { success: true, plans: [...] }
      if (response.data?.plans && Array.isArray(response.data.plans)) {
        setPlans(response.data.plans);
      } else if (Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setPlans([]);
    }
  };

  const fetchUserSubscriptions = async (userId) => {
    try {
      const response = await planAPI.getUserSubscriptions(userId);
      setUserSubscriptions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user subscriptions:', error);
    }
  };

  const handleOpenPlanMappingModal = async (user) => {
    setMappingUser(user);
    await fetchUserSubscriptions(user.id);
    setShowInactiveSubs(false);
    setMappingFormData({
      plan_id: '',
      auto_renew: false
    });
    setShowPlanMappingModal(true);
  };

  const handlePlanMappingSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await planAPI.subscribeUser({
        user_id: mappingUser.id,
        plan_id: parseInt(mappingFormData.plan_id),
        auto_renew: mappingFormData.auto_renew
      });
      
      // PROMPT-132: Show appropriate success message
      if (response.data.plan_replaced) {
        setSuccess(response.data.message || 'Plan switched successfully');
      } else {
        setSuccess('Plan mapped successfully');
      }
      
      setShowPlanMappingModal(false);
      await fetchUserSubscriptions(mappingUser.id);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to map plan');
    }
  };

  // SMS Rate Modal Handlers
  const handleOpenRateModal = async (user) => {
    setRateUser(user);
    setCurrentRate(null);
    setRateFormData({ price_per_sms: '', currency: 'INR' });
    try {
      const response = await pricingAPI.getUserPricing(user.id);
      const activeRate = (response.data || []).find(r => r.is_active);
      if (activeRate) {
        setCurrentRate(activeRate);
        setRateFormData({ price_per_sms: activeRate.price_per_sms.toString(), currency: activeRate.currency || 'INR' });
      }
    } catch (err) {
      console.error('Failed to fetch user pricing:', err);
    }
    setShowRateModal(true);
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      await pricingAPI.update({
        user_id: rateUser.id,
        price_per_sms: parseFloat(rateFormData.price_per_sms),
        currency: rateFormData.currency,
        route_type: 'default'
      });
      setSuccess(`SMS rate updated for ${rateUser.username}`);
      setShowRateModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update rate');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        await userAPI.update(editingUser.id, formData);
        
        // Update user permissions if advanced role is enabled
        if (showAdvancedRole) {
          // First remove existing permission overrides
          const currentPermissions = await smsAPI.getUserPermissionOverrides(editingUser.id);
          for (const override of currentPermissions.data || []) {
            await smsAPI.removePermissionOverride(override.id);
          }
          
          // Then grant new permission overrides
          for (const permissionId of formData.permission_ids) {
            await smsAPI.grantPermissionOverride({
              user_id: editingUser.id,
              permission_id: permissionId
            });
          }
        }
        
        setSuccess('User updated successfully');
      } else {
        const newUser = await userAPI.create(formData);
        
        // Set user permissions if advanced role is enabled
        if (showAdvancedRole && formData.permission_ids.length > 0) {
          for (const permissionId of formData.permission_ids) {
            await smsAPI.grantPermissionOverride({
              user_id: newUser.data.id,
              permission_id: permissionId
            });
          }
        }
        
        setSuccess('User created successfully');
      }
      fetchUsers();
      handleCloseModal();
    } catch (error) {
      setError(error.response?.data?.error || 'Operation failed');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  const handleModuleToggle = (modulePermissions) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => 
      formData.permission_ids.includes(id)
    );

    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePermissionIds.includes(id))
        : [...new Set([...prev.permission_ids, ...modulePermissionIds])]
    }));
  };

  const handleEdit = async (user) => {
    setEditingUser(user);
    
    // Fetch user permission overrides
    try {
      const response = await smsAPI.getUserPermissionOverrides(user.id);
      const userPermissions = response.data || [];
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        full_name: user.full_name || '',
        mobile: user.mobile || '',
        company_name: user.company_name || '',
        tps_limit: user.tps_limit || 10,
        permission_ids: userPermissions.map(p => p.permission_id),
        gui_allowed_ips: user.gui_allowed_ips || ''
      });
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role: user.role,
        full_name: user.full_name || '',
        mobile: user.mobile || '',
        company_name: user.company_name || '',
        tps_limit: user.tps_limit || 10,
        permission_ids: [],
        gui_allowed_ips: user.gui_allowed_ips || ''
      });
    }

    setShowModal(true);
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Are you sure you want to disable this user?')) return;
    
    try {
      await userAPI.disable(id);
      setSuccess('User disabled successfully');
      fetchUsers();
    } catch (error) {
      setError('Failed to disable user');
    }
  };

  const handleEnable = async (id) => {
    try {
      await userAPI.enable(id);
      setSuccess('User enabled successfully');
      fetchUsers();
    } catch (error) {
      setError('Failed to enable user');
    }
  };

  const handleOpenAPIKeyModal = (user) => {
    setApiKeyUser(user);
    setNewAPIKey('');
    setCopiedAPIKey(false);
    setApiKeyLength(8);
    setApiKeyLengthError('');
    setShowAPIKeyModal(true);
  };

  const handleCloseAPIKeyModal = () => {
    setShowAPIKeyModal(false);
    setApiKeyUser(null);
    setNewAPIKey('');
    setCopiedAPIKey(false);
    setApiKeyLength(8);
    setApiKeyLengthError('');
  };

  const handleRegenerateAPIKey = async () => {
    // Validate API key length
    const length = parseInt(apiKeyLength);
    if (isNaN(length) || length < 8 || length > 120) {
      setApiKeyLengthError('API key length must be between 8 and 120 characters');
      return;
    }
    
    setApiKeyLengthError('');
    
    try {
      const response = await userAPI.regenerateAPIKey(apiKeyUser.id, length);
      setNewAPIKey(response.data.api_key);
      setSuccess('API Key regenerated successfully!');
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to regenerate API key');
    }
  };

  const handleCopyAPIKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedAPIKey(true);
    setTimeout(() => setCopiedAPIKey(false), 2000);
  };

  const handleOpenEntityMappingModal = async (user) => {
    setEntityMappingUser(user);
    setShowEntityMappingModal(true);
    
    try {
      // Fetch all entities
      const entitiesResponse = await entityAPI.getAll();
      setAllEntities(entitiesResponse.data || []);
      
      // Fetch user's current entities
      const userEntitiesResponse = await entityAPI.getAdminUserEntities(user.id);
      const userEntityData = userEntitiesResponse.data || [];
      setUserEntities(userEntityData);
      
      // PROMPT-075: Preselect assigned entities
      const assignedEntityIds = userEntityData.map(entity => entity.id);
      setSelectedEntityIds(assignedEntityIds);
      
      console.log('Preselected entities for user', user.username, ':', assignedEntityIds);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
      setError('Failed to load entities');
      // Default to empty selection on error
      setSelectedEntityIds([]);
    }
  };

  const handleCloseEntityMappingModal = () => {
    setShowEntityMappingModal(false);
    setEntityMappingUser(null);
    setAllEntities([]);
    setSelectedEntityIds([]);
    setUserEntities([]);
    setEntitySearchTerm('');
  };

  const handleEntityToggle = (entityId) => {
    setSelectedEntityIds(prev =>
      prev.includes(entityId)
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const handleEntityMappingSubmit = async (e) => {
    e.preventDefault();
    
    // Allow empty selection (removes all entities)
    if (selectedEntityIds.length === 0) {
      if (!window.confirm('No entities selected. This will remove all entity assignments for this user. Continue?')) {
        return;
      }
    }
    
    try {
      console.log('Assigning entities:', {
        userId: entityMappingUser.id,
        entityIds: selectedEntityIds
      });
      
      const response = await entityAPI.bulkMapUserToEntities(entityMappingUser.id, selectedEntityIds);
      console.log('Assignment response:', response);
      
      if (selectedEntityIds.length === 0) {
        setSuccess(`All entity assignments removed for ${entityMappingUser.username}`);
      } else {
        setSuccess(`Successfully assigned ${selectedEntityIds.length} ${selectedEntityIds.length === 1 ? 'entity' : 'entities'} to ${entityMappingUser.username}`);
      }
      
      handleCloseEntityMappingModal();
      fetchUsers();
    } catch (error) {
      console.error('Entity assignment error:', error);
      console.error('Error response:', error.response);
      setError(error.response?.data?.error || error.message || 'Failed to assign entities');
    }
  };

  const handleOpenPasswordModal = (user) => {
    setChangingPasswordUser(user);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setChangingPasswordUser(null);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setShowPassword(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setError('');

    // Validation
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      await userAPI.update(changingPasswordUser.id, { password: passwordData.newPassword });
      setSuccess(`Password changed successfully for ${changingPasswordUser.username}`);
      handleClosePasswordModal();
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    }
  };

  // PROMPT-134: SMPP Configuration Handlers
  const handleOpenSMPPModal = async (user) => {
    setSmppUser(user);
    setSmppError('');
    
    // Fetch existing SMPP configuration if any
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/super/users/${user.id}/smpp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSmppConfig({
          system_id: data.system_id || `${user.username}_smpp`,
          password: '', // Never show existing password
          bind_type: data.bind_type || 'TRX',
          tps_limit: data.tps_limit || 10,
          allowed_ips: data.allowed_ips || '',
          port: data.port || 2775,
          system_type: data.system_type || '',
          enforce_balance_check: data.enforce_balance_check || false,
          status: data.status || 'disabled'
        });
      } else {
        // Set defaults for new configuration
        setSmppConfig({
          system_id: `${user.username}_smpp`,
          password: '',
          bind_type: 'TRX',
          tps_limit: 10,
          allowed_ips: '',
          port: 2775,
          system_type: '',
          enforce_balance_check: false,
          status: 'disabled'
        });
      }
    } catch (error) {
      // Set defaults on error
      setSmppConfig({
        system_id: `${user.username}_smpp`,
        password: '',
        bind_type: 'TRX',
        tps_limit: 10,
        allowed_ips: '',
        port: 2775,
        system_type: '',
        enforce_balance_check: false,
        status: 'disabled'
      });
    }
    
    setShowSMPPModal(true);
  };

  const handleCloseSMPPModal = () => {
    setShowSMPPModal(false);
    setSmppUser(null);
    setSmppError('');
    setShowSMPPPassword(false);
  };

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setSmppConfig({ ...smppConfig, password });
  };

  const handleSMPPConfigSave = async (e) => {
    e.preventDefault();
    setSmppError('');
    setError('');
    
    // Validation
    if (!smppConfig.system_id.trim()) {
      setSmppError('SMPP Username is required');
      return;
    }
    
    if (!smppConfig.password.trim()) {
      setSmppError('SMPP Password is required');
      return;
    }
    
    if (smppConfig.tps_limit < 1 || smppConfig.tps_limit > 1000) {
      setSmppError('TPS Limit must be between 1 and 1000');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/super/users/${smppUser.id}/smpp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(smppConfig)
      });
      
      if (response.ok) {
        setSuccess('SMPP access configured successfully');
        handleCloseSMPPModal();
        fetchUsers();
      } else {
        const error = await response.json();
        setSmppError(error.error || 'Failed to configure SMPP access');
      }
    } catch (error) {
      setSmppError('Failed to configure SMPP access');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setShowAdvancedRole(false);
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      full_name: '',
      mobile: '',
      company_name: '',
      tps_limit: 10,
      permission_ids: [],
      gui_allowed_ips: ''
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      master_admin: 'bg-purple-100 text-purple-800',
      support: 'bg-blue-100 text-blue-800',
      account: 'bg-green-100 text-green-800',
      user: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Generate dropdown options
  const userOptions = [
    { value: '', label: 'All Users' },
    ...users.map(u => ({
      value: u.id.toString(),
      label: `${u.username} (${u.email})`
    }))
  ];

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'master_admin', label: 'Master Admin' },
    { value: 'support', label: 'Support' },
    { value: 'account', label: 'Account' },
    { value: 'user', label: 'User' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const planOptions = plans.map(p => ({
    value: p.id.toString(),
    label: `${p.plan_name} - ${p.currency} ${parseFloat(p.price).toFixed(6).replace(/\.?0+$/, '')} (${p.sms_limit} SMS)`
  }));

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUserId, filterRole, filterStatus]);

  // Filter and search users
  const filteredUsers = users.filter(user => {
    const matchesUser = !selectedUserId || user.id.toString() === selectedUserId;
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);

    return matchesUser && matchesRole && matchesStatus;
  });

  // Client-side pagination on filtered results
  const clientTotalPages = Math.ceil(filteredUsers.length / pageSize) || 1;
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="bg-white rounded-xl shadow-md p-4 md:p-6 max-w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-5 h-5" />
          Add User
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <SearchableDropdown
            options={userOptions}
            value={selectedUserId}
            onChange={setSelectedUserId}
            placeholder="Search users..."
            clearable={true}
          />
        </div>

        <SearchableDropdown
          options={roleOptions}
          value={filterRole}
          onChange={setFilterRole}
          placeholder="Filter by role..."
          clearable={true}
        />

        <SearchableDropdown
          options={statusOptions}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder="Filter by status..."
          clearable={true}
        />
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {paginatedUsers.length} of {filteredUsers.length} users (page {currentPage} of {clientTotalPages})
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div className="hidden md:block overflow-x-auto w-full">
            <table className="w-full divide-y divide-gray-200 table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TPS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No users found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, index) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                      <div className="text-sm text-gray-500">{user.full_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                      <div className="text-sm text-gray-500">{user.mobile || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatIndianCurrency(user.balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.tps_limit || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_active ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        {user.role === 'user' && (
                          <button
                            onClick={() => handleOpenSMPPModal(user)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Enable SMPP Access"
                          >
                            <Icons.Zap className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenPasswordModal(user)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Change Password"
                        >
                          <LockClosedIcon className="w-5 h-5" />
                        </button>
                        {user.role === 'user' && (
                          <button
                            onClick={() => handleOpenAPIKeyModal(user)}
                            className="text-green-600 hover:text-green-900"
                            title="View/Regenerate API Key"
                          >
                            <KeyIcon className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenPlanMappingModal(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Mapping Plan"
                        >
                          <DocumentTextIcon className="w-5 h-5" />
                        </button>
                        {user.role === 'user' && (
                          <button
                            onClick={() => handleOpenEntityMappingModal(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Assign Entities"
                          >
                            <BuildingOfficeIcon className="w-5 h-5" />
                          </button>
                        )}
                        {user.role === 'user' && (
                          <button
                            onClick={() => handleOpenRateModal(user)}
                            className="text-amber-600 hover:text-amber-900"
                            title="SMS Rate"
                          >
                            <CurrencyDollarIcon className="w-5 h-5" />
                          </button>
                        )}
                        {user.is_active ? (
                          <button
                            onClick={() => handleDisable(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Disable"
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleEnable(user.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Enable"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {paginatedUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No users found matching your search criteria
              </div>
            ) : (
              paginatedUsers.map((user, index) => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  {/* User Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">{user.username}</span>
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">{user.full_name || '-'}</div>
                    </div>
                    <div className="text-sm text-gray-500">
                      #{(currentPage - 1) * pageSize + index + 1}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1 mb-3">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    {user.mobile && (
                      <div className="text-sm text-gray-500">{user.mobile}</div>
                    )}
                  </div>

                  {/* Balance and TPS */}
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Balance</div>
                      <div className="text-sm font-medium text-gray-900">{formatIndianCurrency(user.balance)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">TPS</div>
                      <div className="text-sm font-medium text-gray-900">{user.tps_limit || 0}</div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    {user.is_active ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="border-t pt-3">
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="flex flex-col items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-5 h-5 mb-1" />
                        <span className="text-xs">Edit</span>
                      </button>
                      {user.role === 'user' && (
                        <button
                          onClick={() => handleOpenSMPPModal(user)}
                          className="flex flex-col items-center justify-center p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Enable SMPP Access"
                        >
                          <Icons.Zap className="w-5 h-5 mb-1" />
                          <span className="text-xs">SMPP</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenPasswordModal(user)}
                        className="flex flex-col items-center justify-center p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="Change Password"
                      >
                        <LockClosedIcon className="w-5 h-5 mb-1" />
                        <span className="text-xs">Password</span>
                      </button>
                      {user.role === 'user' && (
                        <button
                          onClick={() => handleOpenAPIKeyModal(user)}
                          className="flex flex-col items-center justify-center p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="View/Regenerate API Key"
                        >
                          <KeyIcon className="w-5 h-5 mb-1" />
                          <span className="text-xs">API Key</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenPlanMappingModal(user)}
                        className="flex flex-col items-center justify-center p-2 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                        title="Mapping Plan"
                      >
                        <DocumentTextIcon className="w-5 h-5 mb-1" />
                        <span className="text-xs">Plan</span>
                      </button>
                      {user.role === 'user' && (
                        <button
                          onClick={() => handleOpenEntityMappingModal(user)}
                          className="flex flex-col items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Assign Entities"
                        >
                          <BuildingOfficeIcon className="w-5 h-5 mb-1" />
                          <span className="text-xs">Entities</span>
                        </button>
                      )}
                      {user.is_active ? (
                        <button
                          onClick={() => handleDisable(user.id)}
                          className="flex flex-col items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Disable"
                        >
                          <XCircleIcon className="w-5 h-5 mb-1" />
                          <span className="text-xs">Disable</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEnable(user.id)}
                          className="flex flex-col items-center justify-center p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Enable"
                        >
                          <CheckCircleIcon className="w-5 h-5 mb-1" />
                          <span className="text-xs">Enable</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* PROMPT-055: Pagination */}
      {!loading && users.length > 0 && (
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={filteredUsers.length}
          totalPages={clientTotalPages}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Change Password - {changingPasswordUser?.username}
              </h3>
              <button
                onClick={handleClosePasswordModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {passwordError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {passwordError}
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength="8"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter new password (min 8 characters)"
                />
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters required</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength="8"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Re-enter new password"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="showPassword" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                  Show Password
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleClosePasswordModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username *
                  </label>
                  <input
                    type="text"
                    required
                    disabled={editingUser}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role *
                  </label>
                  {editingUser ? (
                    <div className="w-full px-3 sm:px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700 min-h-[40px] flex items-center">
                      {formData.role}
                    </div>
                  ) : (
                    <SearchableDropdown
                      options={[
                        { value: 'user', label: 'User' },
                        { value: 'support', label: 'Support' },
                        { value: 'account', label: 'Account' },
                        { value: 'master_admin', label: 'Master Admin' }
                      ]}
                      value={formData.role}
                      onChange={(value) => setFormData({ ...formData, role: value })}
                      placeholder="Select Role *"
                      clearable={false}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile
                  </label>
                  <input
                    type="text"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    TPS Limit
                  </label>
                  <input
                    type="number"
                    value={formData.tps_limit}
                    onChange={(e) => setFormData({ ...formData, tps_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Advanced Role Management */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Advanced Role Management</h4>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedRole(!showAdvancedRole)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {showAdvancedRole ? 'Hide' : 'Show'} Permissions
                  </button>
                </div>

                {showAdvancedRole && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Configure specific permissions for this user. When enabled, these permissions override the default role permissions.
                    </p>
                    
                    <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-white">
                      {permissions.map((group) => (
                        <div key={group.module} className="border-b border-gray-200 pb-3 last:border-0">
                          <div className="flex items-center justify-between mb-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={group.permissions.every(p => formData.permission_ids.includes(p.id))}
                                onChange={() => handleModuleToggle(group.permissions)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-900 capitalize">
                                {group.module}
                              </span>
                            </label>
                            <span className="text-xs text-gray-500">
                              {group.permissions.filter(p => formData.permission_ids.includes(p.id)).length}/{group.permissions.length}
                            </span>
                          </div>
                          <div className="ml-6 space-y-2">
                            {group.permissions.map((permission) => (
                              <label key={permission.id} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.permission_ids.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                  {permission.permission_name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({permission.action})
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {formData.permission_ids.length > 0 && (
                      <p className="mt-3 text-sm text-green-600">
                        {formData.permission_ids.length} permission(s) selected
                      </p>
                    )}
                    
                    {/* GUI IP Access Control */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GUI Login IP Restrictions
                      </label>
                      <textarea
                        type="text"
                        value={formData.gui_allowed_ips}
                        onChange={(e) => setFormData({ ...formData, gui_allowed_ips: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        rows={3}
                        placeholder="Enter allowed IP addresses, one per line&#10;Example:&#10;192.168.1.100&#10;10.0.0.0/8&#10;203.0.113.0/24"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Leave empty to allow access from any IP. Supports individual IPs and CIDR notation.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan Mapping Modal */}
      {showPlanMappingModal && mappingUser && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowPlanMappingModal(false)}></div>

            {/* Modal */}
            <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg max-h-[90vh] flex flex-col">
              <form onSubmit={handlePlanMappingSubmit} className="flex flex-col h-full">
                {/* Header */}
                <div className="flex-shrink-0 bg-white px-4 sm:px-6 pt-5 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">
                      Map Plan to User
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowPlanMappingModal(false)}
                      className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                      <span className="text-2xl">&times;</span>
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    User: <span className="font-semibold text-gray-900">{mappingUser.username}</span>
                  </p>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto bg-white px-4 sm:px-6 py-5">
                  <div className="space-y-5">
                    {/* Current Subscriptions (Active Only) */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Current Subscriptions</h4>
                      {(() => {
                        const activeSubs = userSubscriptions.filter(s => s.status === 'ACTIVE');
                        const inactiveSubs = userSubscriptions.filter(s => s.status !== 'ACTIVE');
                        return (
                          <>
                            {activeSubs.length > 0 ? (
                              <div className="space-y-2">
                                {activeSubs.map((sub) => (
                                  <div key={sub.id} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="font-semibold text-sm text-gray-900">{sub.plan?.plan_name}</span>
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                                        {sub.status}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      SMS: {sub.remaining_sms} | Auto-renew: {sub.auto_renew ? 'Yes' : 'No'} | Date: {new Date(sub.updated_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center">
                                <p className="text-sm text-gray-500">No active subscriptions</p>
                              </div>
                            )}

                            {/* Inactive Subscriptions - Collapsible */}
                            {inactiveSubs.length > 0 && (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() => setShowInactiveSubs(!showInactiveSubs)}
                                  className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                  <svg
                                    className={`w-3.5 h-3.5 transition-transform duration-200 ${showInactiveSubs ? 'rotate-90' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  Inactive Subscriptions ({inactiveSubs.length})
                                </button>
                                {showInactiveSubs && (
                                  <div className="mt-2 space-y-2 max-h-32 overflow-y-auto pr-1">
                                    {inactiveSubs.map((sub) => (
                                      <div key={sub.id} className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg opacity-75">
                                        <div className="flex justify-between items-center mb-0.5">
                                          <span className="font-medium text-xs text-gray-700">{sub.plan?.plan_name}</span>
                                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-gray-500">
                                            {sub.status}
                                          </span>
                                        </div>
                                        <div className="text-[11px] text-gray-500">
                                          SMS: {sub.remaining_sms} | Date: {new Date(sub.updated_at).toLocaleDateString()}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Select Plan Field */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Select Plan *
                      </label>
                      <div className="w-full">
                        <SearchableDropdown
                          options={planOptions}
                          value={mappingFormData.plan_id}
                          onChange={(value) => setMappingFormData({...mappingFormData, plan_id: value})}
                          placeholder="Select a plan *"
                          clearable={false}
                        />
                      </div>
                    </div>

                    {/* Auto-renew Checkbox */}
                    <div className="flex items-center p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="auto_renew"
                        checked={mappingFormData.auto_renew}
                        onChange={(e) => setMappingFormData({...mappingFormData, auto_renew: e.target.checked})}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="auto_renew" className="ml-3 block text-sm font-medium text-gray-900">
                        Enable auto-renew subscription
                      </label>
                    </div>
                  </div>
                </div>

                {/* Fixed Footer */}
                <div className="flex-shrink-0 bg-gray-50 px-4 sm:px-6 py-3 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors min-h-[40px]"
                  >
                    Map Plan
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPlanMappingModal(false)}
                    className="w-full sm:w-auto mt-3 sm:mt-0 inline-flex justify-center items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors min-h-[40px]"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {showAPIKeyModal && apiKeyUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                API Key - {apiKeyUser.username}
              </h3>
              <button
                onClick={handleCloseAPIKeyModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              {/* Current API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current API Key
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm break-all">
                    {apiKeyUser.api_key || 'No API key generated'}
                  </div>
                  {apiKeyUser.api_key && (
                    <button
                      onClick={() => handleCopyAPIKey(apiKeyUser.api_key)}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedAPIKey ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* New API Key (after regeneration) */}
              {newAPIKey && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-green-900 mb-2">
                        New API Key Generated!
                      </h4>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 p-3 bg-white border border-green-300 rounded-lg font-mono text-sm break-all">
                          {newAPIKey}
                        </div>
                        <button
                          onClick={() => handleCopyAPIKey(newAPIKey)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedAPIKey ? (
                            <CheckCircleIcon className="w-5 h-5" />
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        ⚠️ Please save this key securely. It will not be shown again.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* API Key Length Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key Length (characters)
                </label>
                <div className="space-y-2">
                  <input
                    type="number"
                    min="8"
                    max="120"
                    value={apiKeyLength}
                    onChange={(e) => {
                      setApiKeyLength(e.target.value);
                      setApiKeyLengthError('');
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      apiKeyLengthError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter length (8-120)"
                  />
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Minimum: 8 characters</span>
                    <span>Maximum: 120 characters</span>
                  </div>
                  {apiKeyLengthError && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {apiKeyLengthError}
                    </div>
                  )}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      💡 <strong>Tip:</strong> Longer keys (64+ characters) provide better security. Default is 8 characters.
                    </p>
                  </div>
                </div>
              </div>

              {/* Warning Message */}
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-yellow-900 mb-1">
                      Important Information
                    </h4>
                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                      <li>Regenerating will invalidate the current API key</li>
                      <li>All applications using the old key will stop working</li>
                      <li>The new key will be shown only once</li>
                      <li>Make sure to update all integrations with the new key</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={handleCloseAPIKeyModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleRegenerateAPIKey}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <KeyIcon className="w-5 h-5" />
                  Regenerate API Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PROMPT-134: SMPP Configuration Modal */}
      {showSMPPModal && smppUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-hidden h-full w-full z-50">
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] md:max-h-none overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">
                  SMPP Access Configuration - {smppUser.username}
                </h3>
                <button
                  onClick={handleCloseSMPPModal}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Conditional Body - Scroll only on mobile/small height */}
              <div className="px-6 py-4 flex-1 overflow-y-auto max-h-[50vh] md:max-h-none md:overflow-visible">
                {smppError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {smppError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Row: SMPP Username */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      SMPP Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={smppConfig.system_id}
                      onChange={(e) => setSmppConfig({ ...smppConfig, system_id: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., user_smpp"
                    />
                  </div>

                  {/* First Row: SMPP Password */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      SMPP Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={showSMPPPassword ? "text" : "password"}
                      value={smppConfig.password}
                      onChange={(e) => setSmppConfig({ ...smppConfig, password: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Enter secure password"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => setShowSMPPPassword(!showSMPPPassword)}
                        className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {showSMPPPassword ? 'Hide' : 'Show'}
                      </button>
                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="px-4 py-2 text-sm text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  {/* Second Row: Bind Type */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Bind Type
                    </label>
                    <select
                      value={smppConfig.bind_type}
                      onChange={(e) => setSmppConfig({ ...smppConfig, bind_type: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    >
                      <option value="TX">TX (Transmitter)</option>
                      <option value="RX">RX (Receiver)</option>
                      <option value="TRX">TRX (Transceiver)</option>
                    </select>
                  </div>

                  {/* Second Row: Max TPS */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Max TPS
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={smppConfig.tps_limit}
                      onChange={(e) => setSmppConfig({ ...smppConfig, tps_limit: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Third Row: Port (Full width on mobile) */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Port
                    </label>
                    <input
                      type="number"
                      value={smppConfig.port}
                      onChange={(e) => setSmppConfig({ ...smppConfig, port: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 md:w-1/3"
                    />
                  </div>

                  {/* Fourth Row: Allowed IPs (Full width) */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Allowed IP(s)
                    </label>
                    <input
                      type="text"
                      value={smppConfig.allowed_ips}
                      onChange={(e) => setSmppConfig({ ...smppConfig, allowed_ips: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., 192.168.1.100,10.0.0.50 (comma separated)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to allow all IPs</p>
                  </div>

                  {/* Fifth Row: System Type (Full width) */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      System Type (Optional)
                    </label>
                    <input
                      type="text"
                      value={smppConfig.system_type}
                      onChange={(e) => setSmppConfig({ ...smppConfig, system_type: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., SMPP"
                    />
                  </div>

                  {/* Checkboxes (Full width) */}
                  <div className="space-y-4 md:col-span-2 pt-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enforce_balance"
                        checked={smppConfig.enforce_balance_check}
                        onChange={(e) => setSmppConfig({ ...smppConfig, enforce_balance_check: e.target.checked })}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="enforce_balance" className="ml-3 text-sm text-gray-700">
                        Enforce Balance Check
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="smpp_status"
                        checked={smppConfig.status === 'enabled'}
                        onChange={(e) => setSmppConfig({ ...smppConfig, status: e.target.checked ? 'enabled' : 'disabled' })}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="smpp_status" className="ml-3 text-sm text-gray-700">
                        SMPP Status Enabled
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="px-6 py-4 border-t bg-gray-50">
                <form onSubmit={handleSMPPConfigSave}>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleCloseSMPPModal}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    >
                      Save Configuration
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entity Mapping Modal */}
      {showEntityMappingModal && entityMappingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Assign Entities - {entityMappingUser.username}
              </h3>
              <button
                onClick={handleCloseEntityMappingModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleEntityMappingSubmit}>
              <div className="space-y-4">
                {/* Current Entities Summary */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <BuildingOfficeIcon className="w-5 h-5" />
                    <span>
                      <strong>{selectedEntityIds.length}</strong> {selectedEntityIds.length === 1 ? 'entity' : 'entities'} selected
                    </span>
                  </div>
                </div>

                {/* Entity Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Entities (Multiple Selection)
                  </label>
                  
                  {/* Search Box */}
                  <div className="mb-3">
                    <div className="relative ml-2">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Icons.Search className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={entitySearchTerm}
                        onChange={(e) => setEntitySearchTerm(e.target.value)}
                        className="block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search entities by name or ID..."
                      />
                      {entitySearchTerm && (
                        <button
                          onClick={() => setEntitySearchTerm('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <XCircleIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {allEntities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No entities available</p>
                    </div>
                  ) : (() => {
                      const filteredEntities = allEntities.filter(entity => 
                        entity.entity_name.toLowerCase().includes(entitySearchTerm.toLowerCase()) ||
                        entity.entity_id.toLowerCase().includes(entitySearchTerm.toLowerCase())
                      );
                      
                      return filteredEntities.length === 0 ? (
                        <div className="border border-gray-300 rounded-lg p-8 text-center text-gray-500">
                          <Icons.Search className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="font-medium">No entities found</p>
                          <p className="text-sm mt-1">Try adjusting your search term</p>
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                          <div className="divide-y divide-gray-200">
                            {filteredEntities.map((entity) => (
                              <label
                                key={entity.id}
                                className={`flex items-center p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  selectedEntityIds.includes(entity.id) ? 'bg-blue-50' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedEntityIds.includes(entity.id)}
                                  onChange={() => handleEntityToggle(entity.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {entity.entity_name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        ID: {entity.entity_id}
                                      </p>
                                    </div>
                                    {entity.is_active ? (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                        Active
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })()
                  }
                </div>

                {/* Helper Text */}
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-700">
                    💡 <strong>Tip:</strong> Select all entities this user should have access to. Previously assigned entities not selected will be removed.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseEntityMappingModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <BuildingOfficeIcon className="w-5 h-5" />
                  Assign Entities ({selectedEntityIds.length})
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SMS Rate Modal */}
      {showRateModal && rateUser && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowRateModal(false)}></div>
            <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md">
              <form onSubmit={handleRateSubmit}>
                <div className="bg-white px-6 pt-5 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <CurrencyDollarIcon className="w-5 h-5 text-amber-600" />
                      SMS Rate
                    </h3>
                    <button type="button" onClick={() => setShowRateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <span className="text-2xl">&times;</span>
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    User: <span className="font-semibold text-gray-900">{rateUser.username}</span>
                  </p>
                </div>

                <div className="bg-white px-6 py-5 space-y-4">
                  {currentRate && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Current Rate</span>
                        <span className="font-bold text-amber-700">
                          {currentRate.currency} {parseFloat(currentRate.price_per_sms).toFixed(4)} / SMS
                        </span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Price per SMS *</label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.0001"
                      required
                      value={rateFormData.price_per_sms}
                      onChange={(e) => setRateFormData({ ...rateFormData, price_per_sms: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="e.g. 0.2500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Currency</label>
                    <select
                      value={rateFormData.currency}
                      onChange={(e) => setRateFormData({ ...rateFormData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="AED">AED - UAE Dirham</option>
                    </select>
                  </div>

                  {rateFormData.price_per_sms && (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
                      <div className="flex justify-between"><span>100 SMS</span><span className="font-medium">{rateFormData.currency} {(parseFloat(rateFormData.price_per_sms) * 100).toFixed(2)}</span></div>
                      <div className="flex justify-between mt-1"><span>1,000 SMS</span><span className="font-medium">{rateFormData.currency} {(parseFloat(rateFormData.price_per_sms) * 1000).toFixed(2)}</span></div>
                      <div className="flex justify-between mt-1"><span>10,000 SMS</span><span className="font-medium">{rateFormData.currency} {(parseFloat(rateFormData.price_per_sms) * 10000).toFixed(2)}</span></div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t border-gray-200">
                  <button type="button" onClick={() => setShowRateModal(false)}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit"
                    className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700">
                    Save Rate
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
